import React, { FunctionComponent, useContext, useMemo } from 'react';

import {
  Input,
  Group,
  FieldError,
  ToggleableField,
  ToggleableFieldProps,
} from '../../components';
import { PaymentsFieldsProps } from './PaymentsFields.types';
import { useWindowSize } from '../../hooks';
import { AuthStore, BrandStore } from '../../context';
import { isPayfacMerchant } from '../../util/merchant.util';

type ProcessingInfoFieldsProps = PaymentsFieldsProps & {
  isShoppingCart?: boolean;
};

export const CardProcessingInfoFields: FunctionComponent<
  ProcessingInfoFieldsProps
> = props => {
  const { isMobile } = useWindowSize();
  const {
    state: { brand },
  } = useContext(BrandStore);
  const {
    state: { auth },
  } = useContext(AuthStore);
  const {
    isShoppingCart,
    form: { mutators },
  } = props;

  const defaultFieldProps: Partial<ToggleableFieldProps<any, HTMLElement>> = {
    toggleable: props.mode === 'review',
    defaultMode: props.mode === 'review' ? 'display' : 'edit',
  };

  const fieldNames = useMemo(() => {
    return isShoppingCart
      ? {
          amex_direct_number: 'amex_direct_number',
          amex_annual_volume: 'amex_annual_volume',
          annual_volume: 'annual_gross_shopping_cart_revenue',
          avg_trans_size: 'avg_shopping_cart_transaction',
          highest_trans_amount: 'largest_shopping_cart_transaction',
          card_present_percent: 'shopping_cart_card_present_percent',
          b2b_percent: 'shopping_cart_b2b_percent',
          international: 'shopping_cart_international',
        }
      : {
          amex_direct_number: 'amex_direct_number',
          amex_annual_volume: 'amex_annual_volume',
          annual_volume: 'annual_volume',
          avg_trans_size: 'avg_trans_size',
          highest_trans_amount: 'highest_trans_amount',
          card_present_percent: 'card_present_percent',
          b2b_percent: 'b2b_percent',
          international: 'international',
        };
  }, [isShoppingCart]);

  return (
    <>
      <Group direction={isMobile ? 'column-reverse' : 'row'}>
        <Group direction="column" space="1rem/0px" style={{ flex: '50%' }}>
          {isPayfacMerchant(auth?.merchant) && (
            <Group direction="column">
              <ToggleableField
                name={fieldNames.amex_direct_number}
                label="If applicable, enter your AMEX Direct SE Number"
                format={v => (v ? v.replace(/[^0-9]/g, '') : '')}
                {...defaultFieldProps}
              >
                {props => (
                  <Input
                    {...props.input}
                    tooltip="AMEX Direct SE Merchant Numbers are provided by AMEX when merchants reach certain processing thresholds."
                  />
                )}
              </ToggleableField>
              <FieldError name={fieldNames.amex_direct_number} />
            </Group>
          )}
          <Group direction="column">
            <ToggleableField
              currency
              name={fieldNames.annual_volume}
              label="Annual Gross CC Revenue $"
              format={v => (v ? v.replace(/[^0-9.,$%]/g, '') : '')}
              {...defaultFieldProps}
            >
              {props => (
                <Input
                  {...props.input}
                  currency
                  tooltip={
                    <>
                      <p>
                        What is your annual gross revenue for credit card
                        transactions?
                      </p>
                      <p>Best estimate to the nearest dollar will work.</p>
                    </>
                  }
                  onChange={e => {
                    props.input.onChange(e);
                    // if form.values.shouldAutoCalculateACHValues, sets the calculated ach fields
                    if (brand?.showAchVolumeFields)
                      mutators.setCalculatedAchFields();
                  }}
                />
              )}
            </ToggleableField>
            <FieldError name={fieldNames.annual_volume} />
          </Group>

          {isPayfacMerchant(auth?.merchant) &&
            (props.values[fieldNames.annual_volume] >= 1000000 ||
              props.values[fieldNames.amex_annual_volume]) && (
              <Group direction="column">
                <ToggleableField
                  currency
                  name={fieldNames.amex_annual_volume}
                  label="Annual Gross AMEX CC Revenue $"
                  format={v => (v ? v.replace(/[^0-9.,$%]/g, '') : '')}
                  {...defaultFieldProps}
                >
                  {props => (
                    <Input
                      {...props.input}
                      currency
                      tooltip={
                        <>
                          <p>
                            What is your annual gross revenue for American
                            Express credit card transactions?
                          </p>
                          <p>Best estimate to the nearest dollar will work.</p>
                        </>
                      }
                    />
                  )}
                </ToggleableField>
                <FieldError name={fieldNames.amex_annual_volume} />
              </Group>
            )}

          <Group direction="column">
            <ToggleableField
              currency
              name={fieldNames.avg_trans_size}
              label="Average CC Transaction Size $"
              format={v => (v ? v.replace(/[^0-9.,$%]/g, '') : '')}
              {...defaultFieldProps}
            >
              {props => (
                <Input
                  {...props.input}
                  currency
                  tooltip={
                    <>
                      <p>
                        What is the amount of the average credit card
                        transaction your business typically runs?
                      </p>
                      <p>Best estimate to the nearest dollar will work.</p>
                    </>
                  }
                  onChange={e => {
                    props.input.onChange(e);
                    // if form.values.shouldAutoCalculateACHValues, sets the calculated ach fields
                    if (brand?.showAchVolumeFields)
                      mutators.setCalculatedAchFields();
                  }}
                />
              )}
            </ToggleableField>
            <FieldError name={fieldNames.avg_trans_size} />
          </Group>

          <Group direction="column">
            <ToggleableField
              currency
              name={fieldNames.highest_trans_amount}
              label="Largest CC Transaction Size $"
              format={v => (v ? v.replace(/[^0-9.,$%]/g, '') : '')}
              {...defaultFieldProps}
            >
              {props => (
                <Input
                  {...props.input}
                  currency
                  tooltip={
                    <>
                      <p>
                        What is the largest credit card transaction your
                        business needs to process?
                      </p>
                      <p>Best estimate to the nearest dollar will work.</p>
                    </>
                  }
                  onChange={e => {
                    props.input.onChange(e);
                    // if form.values.shouldAutoCalculateACHValues, sets the calculated ach fields
                    if (brand?.showAchVolumeFields)
                      mutators.setCalculatedAchFields();
                  }}
                />
              )}
            </ToggleableField>
            <FieldError name={fieldNames.highest_trans_amount} />
          </Group>

          {/* Above */}
          <Group direction="column">
            <ToggleableField
              name={fieldNames.card_present_percent}
              label="Swiped %"
              format={v => (v ? v.replace(/[^0-9.,$%]/g, '') : '')}
              {...defaultFieldProps}
            >
              {props => (
                <Input
                  {...props.input}
                  min="0"
                  max="100"
                  tooltip="What percentage of credit card transactions do you typically take in-person, not keyed-in, either on a physical terminal or on a mobile reader?"
                />
              )}
            </ToggleableField>
            <FieldError name={fieldNames.card_present_percent} />
          </Group>

          <Group direction="column">
            <ToggleableField
              name={fieldNames.b2b_percent}
              label="Business to Business %"
              format={v => (v ? v.replace(/[^0-9.,$%]/g, '') : '')}
              {...defaultFieldProps}
            >
              {props => (
                <Input
                  {...props.input}
                  min="0"
                  max="100"
                  tooltip={
                    <>
                      <p>
                        What percentage of b2b credit card transactions does
                        your business typically take?
                      </p>
                      <p>If you do not know, please enter 0.</p>
                    </>
                  }
                />
              )}
            </ToggleableField>
            <FieldError name={fieldNames.b2b_percent} />
          </Group>

          <Group direction="column">
            <ToggleableField
              name={fieldNames.international}
              label="International Card %"
              format={v => (v ? v.replace(/[^0-9.,$%]/g, '') : '')}
              {...defaultFieldProps}
            >
              {props => (
                <Input
                  {...props.input}
                  min="0"
                  max="100"
                  tooltip={
                    <>
                      <p>
                        What percentage of international credit card
                        transactions does your business typically take?
                      </p>
                      <p>If you do not know, please enter 0.</p>
                    </>
                  }
                />
              )}
            </ToggleableField>
            <FieldError name={fieldNames.international} />
          </Group>
        </Group>
      </Group>
    </>
  );
};
